import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { CancelToken } from 'axios';
import { useState } from 'react';

import { AttendanceCode, AttendanceRegister, RegisterComposition } from './apiTypes/attendance';
import {
  AttendanceEntriesForDateResponse,
  JustificationStatus,
  AttendanceRegistersResponse,
  AttendanceRegisterStatisticsResponse,
  AttendanceStatisticsRequest,
  ConfigureAttendanceFrequenciesParams,
  ConfigureFrequenciesResponse,
  AttendanceStatisticsResponse,
  CreateAttendanceCodesResponse,
  CreateEntriesForRelationResponse,
  EditAttendanceCodeResponse,
  GetAttendanceCountsRequest,
  GetAttendanceCountsResponse,
  GetRelationAttendanceAggregateRequest,
  GetRelationAttendanceAggregateResponse,
  GetRelationTrackingPointsAggregateRequest,
  GetRelationTrackingPointsAggregateResponse,
  CreateAttendanceRegisterRequest,
  DeleteAttendanceRegisterResponse,
  EditAttendanceRegisterRequest,
  EditAttendanceRegisterResponse,
  GetEligibleRegisterGroupsRequest,
  GetEligibleRegisterGroupsResponse,
  GetRegisterCompositionResponse,
  GetGroupAttendanceAggregateRequest,
  GetGroupAttendanceAggregateResponse,
  JustificationsResponse,
  AttendanceEntriesCreateRequest,
  AttendanceEntriesCreateResponse,
  AttendanceEntriesForDateRequest,
  ChangeAttendanceCodeOrderRequest,
  ExportFireRegisterRequestQuery,
  ExportType,
  RemoveAttendanceCodeRequest,
  UpdateAttendanceEntriesForRelationRequest,
} from './apiTypes/endpoints/attendance';
import { ApiError, SORT_DIRECTION } from './apiTypes/misc';

import {
  attendanceCodesCreatePath,
  attendanceCodesDeletePath,
  attendanceCodesEditPath,
  attendanceCodesListPath,
  attendanceCodesOrderPath,
  attendanceCountsPath,
  configureAttendanceFrequenciesPath,
  createRegisterForSchoolPath,
  deleteRegisterForSchoolPath,
  editRegisterForSchoolPath,
  attendanceEntriesCreatePath,
  attendanceEntriesForDateListPath,
  attendanceEntryCreatePath,
  attendanceRegisterPath,
  registerCompositionPath,
  attendanceRegisterStatisticsPath,
  attendanceRegistersListPath,
  eligibleRegisterGroupsPath,
  exportAttendanceForSchoolPath,
  exportFireRegisterPath,
  groupAttendanceAggregatePath,
  attendanceStatisticsPath,
  justificationsListPath,
  relationAttendanceAggregatePath,
  relationTrackingPointsAggregatePath,
} from './apiTypes/paths/attendance';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import { FilterKeys, UserFilter } from './apiTypes/users';
import * as api from './requests';
import { getSortParams } from './utils/getSortParam';
import { removeObjectEmptyArrayValues } from './utils/removeObjectEmptyArrayValues';
import { removeObjectUndefinedNullValues } from './utils/removeObjectUndefinedNullValues';

const DEFAULT_PAGE_SIZE = 50;

const ATTENDANCE_URL = '/attendance';

export function getAttendanceCodes(schoolId: string): Promise<AttendanceCode[]> {
  return api.get(attendanceCodesListPath({ school_id: schoolId }));
}

export const GET_ATTENDANCE_CODES_QUERY = `${ATTENDANCE_URL}GET_ATTENDANCE_CODES__QUERY`;

export const useGetAttendanceCodesQuery = (
  schoolId: string,
  options?: RQUseQueryOptions<AttendanceCode[]>,
) => {
  return useQuery<AttendanceCode[], ApiError>(
    [GET_ATTENDANCE_CODES_QUERY, schoolId],
    () => getAttendanceCodes(schoolId),
    { ...options },
  );
};

export function createAttendanceCode(
  schoolId: string,
  attendanceCodes: Omit<AttendanceCode, 'id'>[],
): Promise<CreateAttendanceCodesResponse> {
  return api.post(attendanceCodesCreatePath({ school_id: schoolId }), attendanceCodes);
}

export function editAttendanceCode({
  id: codeId,
  ...code
}: AttendanceCode): Promise<EditAttendanceCodeResponse> {
  return api.patch(attendanceCodesEditPath({ attendance_code_id: codeId }), code);
}

export function changeAttendanceCodesOrder({
  schoolId,
  order,
}: ChangeAttendanceCodeOrderRequest): Promise<{ success: string }> {
  return api.patch(attendanceCodesOrderPath({ school_id: schoolId }), order);
}

export function removeAttendanceCode({
  codeId,
  confirmed,
}: RemoveAttendanceCodeRequest): Promise<{ success: string; code_to_delete?: string }> {
  return api.remove(attendanceCodesDeletePath({ code_id: codeId }), undefined, {
    params: { confirmed },
  });
}

export interface AttendanceRegisterRequest {
  schoolId: string;
  groupIds?: string[];
  query?: string;
  filters?: Partial<UserFilter>;
  pageSize?: number;
  pageNumber?: number;
  token?: CancelToken;
}

export type AttendanceExportForPageRequest = Pick<
  AttendanceRegisterRequest,
  'filters' | 'schoolId'
>;

const getAttendanceQueryFilters = ({
  query,
  filters,
  groupIds,
}: Omit<AttendanceRegisterRequest, 'schoolId'>) => {
  return {
    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1],
    group_ids: groupIds?.join(','),
    search_query: query || undefined,
    age_group_ids: filters?.age_group?.join(','),
  };
};

export function getAttendanceRegisters({
  schoolId,
  groupIds,
  query,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  pageNumber = 1,
  token,
}: AttendanceRegisterRequest): Promise<AttendanceRegistersResponse> {
  const params = getAttendanceQueryFilters({ filters, groupIds, query });

  return api.get(attendanceRegistersListPath({ school_id: schoolId }), {
    params: {
      ...params,
      page_size: pageSize,
      page_number: pageNumber,
    },
    cancelToken: token,
  });
}

export async function checkAttendanceRegisterDateExists({
  schoolId,
  registerDate,
}: {
  schoolId: string;
  registerDate: string;
}): Promise<boolean> {
  return getAttendanceRegisters({
    schoolId,
    pageSize: 1,
    pageNumber: 1,
    filters: { date: [registerDate, registerDate] },
  }).then((response) => response.count > 0);
}

export const GET_ATTENDANCE_REGISTERS_QUERY = `${ATTENDANCE_URL}/GET_ATTENDANCE_REGISTERS_QUERY`;

export const ATTENDANCE_REGISTERS_FILTER_KEYS = [FilterKeys.Date] as const;

export type GetAttendanceRegistersQueryFilters = {
  [FilterKeys.Date]?: string[];
  [FilterKeys.AgeGroup]?: string[];
};

export const useGetAttendanceRegistersQuery = (
  initialParams: Omit<AttendanceRegisterRequest, 'filters'> & {
    filters: GetAttendanceRegistersQueryFilters;
  },
  options?: RQUseInfiniteQueryOptions<AttendanceRegistersResponse>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<AttendanceRegistersResponse, ApiError>(
    [
      GET_ATTENDANCE_REGISTERS_QUERY,
      { ...params, filters: removeObjectEmptyArrayValues(params.filters) },
    ],
    ({ pageParam }) =>
      getAttendanceRegisters({
        pageNumber: pageParam,
        ...params,
        filters: removeObjectEmptyArrayValues(params.filters),
      }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export async function getAttendanceStatistics({
  schoolId,
  groupId,
  dateFrom,
  dateTo,
  token,
  ageGroups,
}: AttendanceStatisticsRequest): Promise<AttendanceStatisticsResponse> {
  return api.get(attendanceStatisticsPath({ school_id: schoolId }), {
    params: {
      group_ids: groupId,
      date_from: dateFrom,
      date_to: dateTo,
      age_group_ids: ageGroups?.length ? ageGroups?.join(',') : undefined,
    },
    cancelToken: token,
  });
}

export const GET_ATTENDANCE_STATS_QUERY = `${ATTENDANCE_URL}/GET_ATTENDANCE_STATS_QUERY`;

export const useGetAttendanceStatisticsQuery = (
  params: AttendanceStatisticsRequest,
  options?: RQUseQueryOptions<AttendanceStatisticsResponse>,
) => {
  return useQuery<AttendanceStatisticsResponse, ApiError>(
    [GET_ATTENDANCE_STATS_QUERY, params],
    () => getAttendanceStatistics(params),
    {
      ...options,
    },
  );
};

export function getAttendanceRegister(registerId: string): Promise<AttendanceRegister> {
  return api.get(attendanceRegisterPath({ register_id: registerId }));
}

export const GET_ATTENDANCE_REGISTER_QUERY = `${ATTENDANCE_URL}/GET_ATTENDANCE_REGISTER_QUERY`;

export const useGetAttendanceRegisterQuery = (
  registerId: string,
  options?: RQUseQueryOptions<AttendanceRegister>,
) => {
  return useQuery<AttendanceRegister, ApiError>(
    [GET_ATTENDANCE_REGISTER_QUERY, registerId],
    () => getAttendanceRegister(registerId),
    {
      ...options,
    },
  );
};

export function getRegisterComposition(
  registerId: string,
): Promise<GetRegisterCompositionResponse> {
  return api.get(registerCompositionPath({ register_id: registerId }));
}

export const GET_REGISTER_COMPOSITION_QUERY = `${ATTENDANCE_URL}/GET_REGISTER_COMPOSITION_QUERY`;

export const useGetRegisterCompositionQuery = (
  registerId: string,
  options?: RQUseQueryOptions<GetRegisterCompositionResponse>,
) => {
  return useQuery<GetRegisterCompositionResponse, ApiError>(
    [GET_REGISTER_COMPOSITION_QUERY, registerId],
    () => getRegisterComposition(registerId),
    {
      ...options,
    },
  );
};

export function getAttendanceRegisterStatistics(
  registerId: string,
  schoolId: string,
): Promise<AttendanceRegisterStatisticsResponse> {
  return api.get(attendanceRegisterStatisticsPath({ register_id: registerId }), {
    params: { school_id: schoolId },
  });
}

export const GET_ATTENDANCE_REGISTER_STATISTICS_QUERY = `${ATTENDANCE_URL}/GET_ATTENDANCE_REGISTER_STATISTICS_QUERY`;

export const useGetAttendanceRegisterStatisticsQuery = (
  registerId: string,
  schoolId: string,
  options?: RQUseQueryOptions<AttendanceRegisterStatisticsResponse>,
) => {
  return useQuery<AttendanceRegisterStatisticsResponse, ApiError>(
    [GET_ATTENDANCE_REGISTER_STATISTICS_QUERY, registerId, schoolId],
    () => getAttendanceRegisterStatistics(registerId, schoolId),
    {
      ...options,
    },
  );
};

type CreateAttendanceRegisterParams = {
  schoolId: string;
  register: CreateAttendanceRegisterRequest;
};

type UpdateAttendanceRegisterParams = {
  id: string;
  register: EditAttendanceRegisterRequest;
  confirmed?: boolean;
};

type DeleteAttendanceRegisterParams = {
  id: string;
  confirmed?: boolean;
};

function createAttendanceRegister(schoolId: string, params: CreateAttendanceRegisterRequest) {
  return api.post(createRegisterForSchoolPath({ school_id: schoolId }), params);
}

export const useCreateAttendanceRegisterMutation = (
  options?: RQUseMutationOptions<RegisterComposition, CreateAttendanceRegisterParams>,
): RQUseMutationResult<RegisterComposition, CreateAttendanceRegisterParams> => {
  return useMutation(
    ({ schoolId, register }) => createAttendanceRegister(schoolId, register),
    options,
  );
};

function editAttendanceRegister(
  registerId: string,
  params: EditAttendanceRegisterRequest,
  confirmed?: boolean,
) {
  return api.patch(editRegisterForSchoolPath({ register_id: registerId }), params, {
    params: { confirmed },
  });
}

export const useEditAttendanceRegisterMutation = (
  options?: RQUseMutationOptions<EditAttendanceRegisterResponse, UpdateAttendanceRegisterParams>,
): RQUseMutationResult<EditAttendanceRegisterResponse, UpdateAttendanceRegisterParams> => {
  return useMutation(
    ({ id, confirmed, register }) => editAttendanceRegister(id, register, confirmed),
    options,
  );
};

function deleteAttendanceRegister(id: string, confirmed?: boolean) {
  return api.remove(deleteRegisterForSchoolPath({ register_id: id }), undefined, {
    params: { confirmed },
  });
}

export const useDeleteAttendanceRegisterMutation = (
  options?: RQUseMutationOptions<DeleteAttendanceRegisterResponse, DeleteAttendanceRegisterParams>,
): RQUseMutationResult<DeleteAttendanceRegisterResponse, DeleteAttendanceRegisterParams> => {
  return useMutation(({ id, confirmed }) => deleteAttendanceRegister(id, confirmed), options);
};

export function getAttendanceEntriesForDate({
  groupId,
  date,
  query,
  token,
}: AttendanceEntriesForDateRequest): Promise<AttendanceEntriesForDateResponse> {
  return api.get(attendanceEntriesForDateListPath({ group_id: groupId }), {
    params: { single_date: date, search_query: query || undefined },
    cancelToken: token,
  });
}

export const GET_ATTENDANCE_ENTRIES_FOR_DATE_QUERY = `${ATTENDANCE_URL}GET_ATTENDANCE_ENTRIES_FOR_DATE_QUERY`;

export const useGetAttendanceEntriesForDateQuery = (
  params: AttendanceEntriesForDateRequest,
  options?: RQUseQueryOptions<AttendanceEntriesForDateResponse>,
) => {
  return useQuery<AttendanceEntriesForDateResponse, ApiError>(
    [GET_ATTENDANCE_ENTRIES_FOR_DATE_QUERY, params],
    () => getAttendanceEntriesForDate(params),
    { ...options },
  );
};

export function getRelationAttendanceAggregate({
  relationId,
  token,
  ...params
}: GetRelationAttendanceAggregateRequest): Promise<GetRelationAttendanceAggregateResponse> {
  return api.get(relationAttendanceAggregatePath({ relation_id: relationId }), {
    params,
    cancelToken: token,
  });
}

export const GET_RELATION_ATTENDANCE_AGGREGATE_QUERY = `${ATTENDANCE_URL}GET_RELATION_ATTENDANCE_AGGREGATE_QUERY`;

export const useGetRelationAttendanceAggregateQuery = (
  params: GetRelationAttendanceAggregateRequest,
  options?: RQUseQueryOptions<GetRelationAttendanceAggregateResponse>,
) => {
  return useQuery<GetRelationAttendanceAggregateResponse, ApiError>(
    [
      GET_RELATION_ATTENDANCE_AGGREGATE_QUERY,
      params.relationId,
      params.school_id,
      { dateFrom: params.date_from, dateTo: params.date_to },
    ],
    () => getRelationAttendanceAggregate(params),
    { ...options },
  );
};

export function getRelationTrackingPointsAggregate({
  relationId,
  token,
  ...params
}: GetRelationTrackingPointsAggregateRequest): Promise<GetRelationTrackingPointsAggregateResponse> {
  return api.get(relationTrackingPointsAggregatePath({ relation_id: relationId }), {
    params: removeObjectUndefinedNullValues(params),
    cancelToken: token,
  });
}

export const GET_RELATION_TRACKING_POINTS_AGGREGATE_QUERY = `${ATTENDANCE_URL}GET_RELATION_TRACKING_POINTS_AGGREGATE_QUERY`;

export const useGetRelationTrackingPointsAggregateQuery = (
  params: GetRelationTrackingPointsAggregateRequest,
  options?: RQUseInfiniteQueryOptions<GetRelationTrackingPointsAggregateResponse>,
) =>
  useInfiniteQuery<GetRelationTrackingPointsAggregateResponse, ApiError>(
    [GET_RELATION_TRACKING_POINTS_AGGREGATE_QUERY, params],
    ({ pageParam }) =>
      getRelationTrackingPointsAggregate({ ...params, page_number: pageParam || 1 }),
    {
      getNextPageParam: (lastPage) => {
        const pageSize = params.page_size ?? lastPage.weeks.length;
        if (!pageSize || lastPage.current_page * pageSize >= lastPage.count) return undefined;
        return lastPage.current_page + 1;
      },
      ...options,
    },
  );

export type TakeAttendanceForGroupParams = {
  groupId: string;
  data: AttendanceEntriesCreateRequest;
};

export function takeAttendanceForGroup({ groupId, data }: TakeAttendanceForGroupParams) {
  return api.post(attendanceEntriesCreatePath({ group_id: groupId }), data);
}

export const useTakeAttendanceForGroupMutation = (
  options?: RQUseMutationOptions<AttendanceEntriesCreateResponse, TakeAttendanceForGroupParams>,
): RQUseMutationResult<AttendanceEntriesCreateResponse, TakeAttendanceForGroupParams> => {
  return useMutation(takeAttendanceForGroup, options);
};

export function updateAttendanceEntriesForRelation({
  registerId,
  relationId,
  data,
}: UpdateAttendanceEntriesForRelationRequest) {
  return api.post(
    attendanceEntryCreatePath({ register_id: registerId, relation_id: relationId }),
    data,
  );
}

export const useUpdateAttendanceEntriesForRelation = (
  options?: RQUseMutationOptions<
    CreateEntriesForRelationResponse,
    UpdateAttendanceEntriesForRelationRequest
  >,
): RQUseMutationResult<
  CreateEntriesForRelationResponse,
  UpdateAttendanceEntriesForRelationRequest
> => {
  return useMutation(updateAttendanceEntriesForRelation, { ...options });
};

const EXPORT_FIRE_REGISTER_ACCEPT: Record<ExportType, string> = {
  [ExportType.Pdf]: 'application/pdf',
  [ExportType.Csv]: 'text/csv',
  [ExportType.Json]: 'application/json',
};

export function exportFireRegister(params: ExportFireRegisterRequestQuery) {
  const exportType = params.export_type ?? ExportType.Pdf;
  const query = removeObjectUndefinedNullValues({ ...params, export_type: exportType });

  return api.get(exportFireRegisterPath(), {
    params: query,
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: EXPORT_FIRE_REGISTER_ACCEPT[exportType],
    },
  });
}

export const exportAttendanceForPage = ({
  schoolId,
  filters,
}: AttendanceExportForPageRequest): Promise<ArrayBuffer> => {
  const params = getAttendanceQueryFilters({ filters });

  return api.get(exportAttendanceForSchoolPath({ school_id: schoolId }), {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params,
  });
};

export const useExportAttendanceForPage = (
  options?: RQUseMutationOptions<ArrayBuffer, AttendanceExportForPageRequest>,
): RQUseMutationResult<ArrayBuffer, AttendanceExportForPageRequest> => {
  return useMutation(
    (params) =>
      exportAttendanceForPage({
        ...params,
        filters: removeObjectEmptyArrayValues(params.filters ?? {}),
      }),
    options,
  );
};

type GetAttendanceJustificationsQueryFilters = {
  [FilterKeys.Date]?: string[];
  relationId?: string;
  statuses?: JustificationStatus[];
};

type GetAttendanceJustificationsQuerySort = {
  columnTextId: 'register_date';
  direction: SORT_DIRECTION;
};

type AttendanceJustificationsRequest = {
  schoolId: string;
  filters: GetAttendanceJustificationsQueryFilters;
  sort?: GetAttendanceJustificationsQuerySort[];
  pageSize?: number;
  pageNumber?: number;
  query?: string;
};

export const getAttendanceJustifications = ({
  schoolId,
  filters,
  sort = [{ columnTextId: 'register_date', direction: SORT_DIRECTION.DESC }],
  pageSize = DEFAULT_PAGE_SIZE,
  pageNumber,
  query,
}: AttendanceJustificationsRequest): Promise<JustificationsResponse> => {
  const params = removeObjectUndefinedNullValues({
    page_number: pageNumber,
    page_size: pageSize,
    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1] || filters?.date?.[0],
    relation_id: filters?.relationId,
    sort_by: getSortParams(sort),
    statuses: filters?.statuses?.join(','),
    search_query: query,
  });

  return api.get(justificationsListPath({ school_id: schoolId }), {
    params,
  });
};

export const GET_ATTENDANCE_JUSTIFICATIONS_QUERY = `${ATTENDANCE_URL}/GET_ATTENDANCE_JUSTIFICATIONS_QUERY`;

export const useGetAttendanceJustificationsQuery = (
  initialParams: AttendanceJustificationsRequest,
  options?: RQUseInfiniteQueryOptions<JustificationsResponse>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<JustificationsResponse, ApiError>(
    [
      GET_ATTENDANCE_JUSTIFICATIONS_QUERY,
      { ...params, filters: removeObjectEmptyArrayValues(params.filters) },
    ],
    ({ pageParam }) =>
      getAttendanceJustifications({
        pageNumber: pageParam,
        ...params,
        filters: removeObjectEmptyArrayValues(params.filters),
      }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export const getAttendanceCounts = ({
  schoolId,
  ...params
}: GetAttendanceCountsRequest): Promise<GetAttendanceCountsResponse> => {
  return api.get(attendanceCountsPath({ school_id: schoolId }), {
    params: removeObjectUndefinedNullValues(params),
  });
};

export const GET_ATTENDANCE_COUNTS_QUERY = `${ATTENDANCE_URL}/GET_ATTENDANCE_COUNTS_QUERY`;

export const useGetAttendanceCountsQuery = (
  params: GetAttendanceCountsRequest,
  options?: RQUseQueryOptions<GetAttendanceCountsResponse>,
) =>
  useQuery<GetAttendanceCountsResponse, ApiError>(
    [GET_ATTENDANCE_COUNTS_QUERY, params],
    () => getAttendanceCounts(params),
    { ...options },
  );

export const configureAttendanceFrequencies = ({
  schoolId,
  ageGroups,
}: ConfigureAttendanceFrequenciesParams): Promise<ConfigureFrequenciesResponse> =>
  api.patch(configureAttendanceFrequenciesPath({ school_id: schoolId }), {
    age_groups: ageGroups,
  });

export const useConfigureAttendanceFrequenciesMutation = (
  options?: RQUseMutationOptions<
    ConfigureFrequenciesResponse,
    ConfigureAttendanceFrequenciesParams
  >,
): RQUseMutationResult<ConfigureFrequenciesResponse, ConfigureAttendanceFrequenciesParams> =>
  useMutation(
    (params: ConfigureAttendanceFrequenciesParams) => configureAttendanceFrequencies(params),
    { ...options },
  );

export const getEligibleRegisterGroups = ({
  schoolId,
  registerDate,
}: GetEligibleRegisterGroupsRequest): Promise<GetEligibleRegisterGroupsResponse> => {
  return api.get(eligibleRegisterGroupsPath({ school_id: schoolId }), {
    params: { register_date: registerDate },
  });
};

export const GET_ELIGIBLE_REGISTER_GROUPS_QUERY = `${ATTENDANCE_URL}/GET_ELIGIBLE_REGISTER_GROUPS_QUERY`;

export const useGetEligibleRegisterGroupsQuery = (
  params: GetEligibleRegisterGroupsRequest,
  options?: RQUseQueryOptions<GetEligibleRegisterGroupsResponse>,
) => {
  return useQuery<GetEligibleRegisterGroupsResponse, ApiError>(
    [GET_ELIGIBLE_REGISTER_GROUPS_QUERY, params],
    () => getEligibleRegisterGroups(params),
    { ...options },
  );
};

export const getGroupAttendanceAggregate = ({
  groupId,
  school_id,
  date_from,
  date_to,
  sort_by,
  page_size,
  page_number,
  session_scope,
  include,
}: GetGroupAttendanceAggregateRequest): Promise<GetGroupAttendanceAggregateResponse> => {
  return api.get(groupAttendanceAggregatePath({ group_id: groupId }), {
    params: removeObjectUndefinedNullValues({
      school_id,
      date_from,
      date_to,
      sort_by,
      page_size,
      page_number,
      session_scope,
      include,
    }),
  });
};

export const GET_GROUP_ATTENDANCE_AGGREGATE_QUERY = `${ATTENDANCE_URL}/GET_GROUP_ATTENDANCE_AGGREGATE_QUERY`;

export const useGetGroupAttendanceAggregateQuery = (
  initialParams: GetGroupAttendanceAggregateRequest,
  options?: RQUseQueryOptions<GetGroupAttendanceAggregateResponse>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useQuery<GetGroupAttendanceAggregateResponse, ApiError>(
    [GET_GROUP_ATTENDANCE_AGGREGATE_QUERY, params],
    () => getGroupAttendanceAggregate(params),
    { ...options },
  );

  return { ...query, setParams, params };
};

export const useGetGroupAttendanceAggregateInfiniteQuery = (
  params: GetGroupAttendanceAggregateRequest,
  options?: RQUseInfiniteQueryOptions<GetGroupAttendanceAggregateResponse>,
) =>
  useInfiniteQuery<GetGroupAttendanceAggregateResponse, ApiError>(
    [GET_GROUP_ATTENDANCE_AGGREGATE_QUERY, params],
    ({ pageParam }) => getGroupAttendanceAggregate({ ...params, page_number: pageParam || 1 }),
    {
      getNextPageParam: (lastPage) => {
        const totalRegisters = lastPage.count;
        const loadedRegisters = lastPage.current_page * (params.page_size || totalRegisters);
        return loadedRegisters < totalRegisters ? lastPage.current_page + 1 : undefined;
      },
      ...options,
    },
  );
